import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

const ApiTable = forwardRef(
  ({ apiSource, columns, defaultOrderBy = '', filters, setLoading, enablePagination = true }, ref) => {
    const [pageIndex, setPageIndex] = useState(0);
    const [data, setData] = useState(null);
    const [orderBy, setOrderBy] = useState(defaultOrderBy);

    const loadData = useCallback(() => {
      setLoading && setLoading(true);
      const params = {
        offset: 15 * pageIndex,
        orderBy: orderBy,
        ...filters,
      };
      apiSource({ params })
        .then(response => {
          setData(response.data);
        })
        .finally(() => setLoading && setLoading(false));
    }, [apiSource, filters, orderBy, pageIndex, setLoading]);

    useEffect(() => {
      loadData();
    }, [apiSource, pageIndex, orderBy, filters, setLoading, loadData]);

    useImperativeHandle(ref, () => ({
      loadData: () => loadData(),
    }));

    const getColumnValueByAccessor = (row, accessor) => {
      return accessor.split('.').reduce(function (r, prop) {
        return r[prop];
      }, row);
    };

    const handleSort = (sortField, event) => {
      if (sortField === orderBy) {
        setOrderBy(`-${sortField}`);
      } else if (`-${sortField}` === orderBy) {
        setOrderBy('');
      } else {
        setOrderBy(sortField);
      }
    };

    const getSortDirection = sortField => {
      if (sortField === orderBy) {
        return 'asc';
      } else if (`-${sortField}` === orderBy) {
        return 'desc';
      }
      return false;
    };

    return (
      <Stack>
        <TableContainer>
          <Table
            sx={{
              '& .MuiTableCell-head': {
                borderBottom: '1px solid black',
              },
              '& .MuiTableCell-root': {
                paddingX: 0.5,
                paddingY: 1,
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map(column => {
                  if (column.enableSortBy) {
                    const sortDirection = getSortDirection(column.sortField);
                    return (
                      <TableCell key={`header${column.header}`} sortDirection={sortDirection}>
                        <TableSortLabel
                          active={sortDirection !== false}
                          direction={sortDirection || 'asc'}
                          onClick={event => handleSort(column.sortField, event)}
                        >
                          {column.header}
                        </TableSortLabel>
                      </TableCell>
                    );
                  }

                  return <TableCell key={`header${column.header}`}>{column.header}</TableCell>;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {data === null || data.results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>Geen resultaten gevonden</TableCell>
                </TableRow>
              ) : (
                data.results.map(result => (
                  <TableRow key={result.id}>
                    {columns.map(column => (
                      <TableCell key={`row${result.id}column${column.header}`}>
                        {column.Cell ? (
                          <column.Cell value={result} />
                        ) : (
                          getColumnValueByAccessor(result, column.accessor)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {enablePagination && data !== null && (
          <TablePagination
            component="div"
            count={parseInt(data.count)}
            page={pageIndex}
            onPageChange={(event, value) => setPageIndex(value)}
            rowsPerPage={15}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
          />
        )}
      </Stack>
    );
  },
);

export default ApiTable;
